import MoreVertIcon from '@assets/images/common/icon/more-vert/more-vert.svg';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type MenuItemVariant = 'default' | 'warning';

type MenuItem = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  variant?: MenuItemVariant;
};

type Props = {
  menuItems: MenuItem[];
  menuWidth?: number;
};

const MoreVertMenu = ({ menuItems, menuWidth = 110 }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleClick = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;
      const menuButtonHeight = 24;

      // ボタンの位置を基準にメニューの位置を設定
      setMenuPosition({
        top: rect.top + scrollY + menuButtonHeight / 2,
        left: rect.right - menuWidth + scrollX, // メニューの幅を考慮
      });
    }
    setIsOpen(!isOpen);
  };

  const getButtonClassName = (variant: MenuItemVariant = 'default', isLast: boolean = false) => {
    const baseClass = 'block select-none bg-white px-6 py-3 text-left text-sm transition-colors';
    const borderClass = isLast ? '' : 'border-b border-gray-200';

    if (variant === 'warning') {
      return `${baseClass} ${borderClass} text-warning-500 hover:bg-warning-500 hover:text-white`;
    }

    return `${baseClass} ${borderClass} hover:bg-primary-500 hover:text-white`;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    // ウィンドウのリサイズ時にメニューの位置を更新
    const handleResize = () => {
      if (buttonRef.current && isOpen) {
        const rect = buttonRef.current.getBoundingClientRect();
        const scrollY = window.scrollY;
        const scrollX = window.scrollX;
        const menuButtonHeight = 24;

        setMenuPosition({
          top: rect.top + scrollY + menuButtonHeight / 2,
          left: rect.right - menuWidth + scrollX,
        });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
    };
  }, [isOpen, menuWidth]);

  return (
    <div className='relative size-full '>
      <button
        ref={buttonRef}
        className='grid size-full place-items-center transition-opacity hover:opacity-60'
        onClick={handleClick}
        aria-expanded={isOpen}
      >
        <img
          src={MoreVertIcon}
          alt=''
          width='24'
          height='24'
          loading='lazy'
        />
      </button>
      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className={`absolute z-50`}
            style={{
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
              width: `${menuWidth}px`,
            }}
          >
            <div className='flex flex-col overflow-hidden rounded border border-solid border-gray-200'>
              {menuItems.map((item, index) => {
                const isLastItem = index === menuItems.length - 1;
                return (
                  <button
                    key={index}
                    className={getButtonClassName(item.variant, isLastItem)}
                    onClick={() => {
                      item.onClick();
                      setIsOpen(false);
                    }}
                    disabled={item.disabled}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default MoreVertMenu;
